"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

const features = [
  "Scalable multi-account architecture",
  "No spending limits",
  "1 business day deployment",
  "Rapid redeployment support",
  "Global platform coverage",
];

export default function BuiltForScaleSection() {
  const ref = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            // Animate network lines
            svgRef.current?.querySelectorAll(".network-line").forEach((line, i) => {
              (line as SVGElement).style.animationDelay = `${i * 0.3}s`;
              (line as SVGElement).style.animationPlayState = "running";
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    ref.current?.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="py-32 bg-[#0B0F14]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left */}
          <div>
            <div className="tag-badge mb-6 reveal">Architecture</div>
            <h2 className="section-title text-[#F0F4F8] mb-10 reveal reveal-delay-1">
              Engineered{" "}
              <span className="text-[#00E676]">For Volume.</span>
            </h2>
            <div className="space-y-4 reveal reveal-delay-2">
              {features.map((f) => (
                <div
                  key={f}
                  className="flex items-center gap-4 p-4 card-neon rounded-xl"
                >
                  <span
                    className="w-2 h-2 rounded-full bg-[#00E676] flex-shrink-0"
                    style={{ boxShadow: "0 0 8px rgba(0,230,118,0.8)" }}
                  />
                  <span className="text-[#F0F4F8] text-sm font-medium">{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Network Diagram SVG */}
          <div className="reveal reveal-delay-3 flex items-center justify-center">
            <svg
              ref={svgRef}
              viewBox="0 0 400 400"
              className="w-full max-w-sm"
              aria-label="Network diagram showing multi-account infrastructure architecture"
            >
              {/* Background glow */}
              <defs>
                <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#00E676" stopOpacity="0.08" />
                  <stop offset="100%" stopColor="#00E676" stopOpacity="0" />
                </radialGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <circle cx="200" cy="200" r="180" fill="url(#centerGlow)" />

              {/* Center node */}
              <circle cx="200" cy="200" r="20" fill="#11161D" stroke="#00E676" strokeWidth="1.5" filter="url(#glow)" />
              <circle cx="200" cy="200" r="6" fill="#00E676" className="network-node" />

              {/* Outer nodes */}
              {[
                { cx: 200, cy: 60, label: "Client" },
                { cx: 340, cy: 140, label: "Account A" },
                { cx: 340, cy: 260, label: "Account B" },
                { cx: 200, cy: 340, label: "Meta" },
                { cx: 60, cy: 260, label: "Account C" },
                { cx: 60, cy: 140, label: "Account D" },
              ].map((node, i) => (
                <g key={node.label}>
                  <line
                    x1="200"
                    y1="200"
                    x2={node.cx}
                    y2={node.cy}
                    stroke="#00E676"
                    strokeWidth="0.8"
                    strokeOpacity="0.3"
                    className="network-line"
                    style={{ animationPlayState: "paused" }}
                  />
                  <circle
                    cx={node.cx}
                    cy={node.cy}
                    r="14"
                    fill="#11161D"
                    stroke="#00E676"
                    strokeWidth="1"
                    strokeOpacity="0.5"
                    filter="url(#glow)"
                  />
                  <circle
                    cx={node.cx}
                    cy={node.cy}
                    r="4"
                    fill="#00E676"
                    fillOpacity="0.7"
                    className="network-node"
                    style={{ animationDelay: `${i * 0.4}s` }}
                  />
                  <text
                    x={node.cx}
                    y={node.cy + 26}
                    textAnchor="middle"
                    fill="#6B7A8D"
                    fontSize="9"
                    fontFamily="JetBrains Mono, monospace"
                    letterSpacing="0.08em"
                  >
                    {node.label}
                  </text>
                </g>
              ))}

              {/* Orbit ring */}
              <circle
                cx="200"
                cy="200"
                r="100"
                fill="none"
                stroke="#00E676"
                strokeWidth="0.5"
                strokeOpacity="0.12"
                strokeDasharray="4 8"
              />
              <circle
                cx="200"
                cy="200"
                r="150"
                fill="none"
                stroke="#00E676"
                strokeWidth="0.3"
                strokeOpacity="0.06"
                strokeDasharray="2 12"
              />
            </svg>
          </div>
        </div>
      </div>
    </motion.section>
  );
}